/**
 * XPRecap - Post-game XP breakdown and level-up animation
 * 
 * Shows:
 * - Itemized XP breakdown by category
 * - Animated progress bar filling
 * - Level-up celebration on level change
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, TrendingUp, Trophy, Zap, Calendar } from 'lucide-react';
import { playSuccess, playClick } from '@/utils/sounds';

// Category icons
const CATEGORY_ICONS = {
  batting: TrendingUp,
  peanut_gallery: Star,
  game_bonus: Trophy,
  daily_bonus: Calendar,
};

// Category colors
const CATEGORY_COLORS = {
  batting: '#4B8B3B',
  peanut_gallery: '#FFD700',
  game_bonus: '#CC0000',
  daily_bonus: '#3333FF',
};

export default function XPRecap({ 
  xpResult, 
  onComplete,
  autoProgress = true 
}) {
  const {
    xp_before = 0,
    xp_after = 0,
    level_before = 1,
    level_after = 1,
    levels_crossed = [],
    breakdown = [],
    total_xp_earned = 0,
  } = xpResult || {};

  const [currentXP, setCurrentXP] = useState(xp_before);
  const [currentLevel, setCurrentLevel] = useState(level_before);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [revealedItems, setRevealedItems] = useState(0);
  const [animationComplete, setAnimationComplete] = useState(false);
  const levelUpQueue = useRef([...levels_crossed]);

  // Reveal breakdown items one by one
  useEffect(() => {
    if (revealedItems < breakdown.length) {
      const timer = setTimeout(() => {
        playClick();
        setRevealedItems(prev => prev + 1);
      }, 300);
      return () => clearTimeout(timer);
    } else if (autoProgress && revealedItems === breakdown.length && breakdown.length > 0) {
      // Start XP fill animation after breakdown reveals
      const timer = setTimeout(() => {
        animateXPFill();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [revealedItems, breakdown.length, autoProgress]);

  const animateXPFill = () => {
    // Animate XP counter from before to after
    const duration = 2000;
    const startTime = Date.now();
    const xpDiff = xp_after - xp_before;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const eased = 1 - Math.pow(1 - progress, 3);
      
      const newXP = Math.round(xp_before + (xpDiff * eased));
      setCurrentXP(newXP);
      
      // Check for level ups during animation
      if (levelUpQueue.current.length > 0) {
        const nextLevelUp = levelUpQueue.current[0];
        // Simplified check - just trigger level ups in order
        if (progress > (levelUpQueue.current.length - levels_crossed.length + 1) / (levels_crossed.length + 1)) {
          levelUpQueue.current.shift();
          setCurrentLevel(nextLevelUp);
          setShowLevelUp(true);
          playSuccess();
          setTimeout(() => setShowLevelUp(false), 1500);
        }
      }
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCurrentXP(xp_after);
        setCurrentLevel(level_after);
        setAnimationComplete(true);
      }
    };
    
    requestAnimationFrame(animate);
  };

  // Calculate progress within level (simplified)
  const getProgressPercent = () => {
    // For simplicity, show progress as percentage toward next 100 XP
    const xpInLevel = currentXP % 100;
    return xpInLevel;
  };

  // Group breakdown by category
  const groupedBreakdown = breakdown.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <div className="xp-recap" data-testid="xp-recap">
      {/* Level Badge & XP Counter */}
      <div className="pixel-card p-6 mb-6 text-center">
        <div className="flex items-center justify-center gap-4 mb-4">
          <div className="relative">
            <div 
              className="w-20 h-20 rounded-full border-4 border-grass flex items-center justify-center"
              style={{ backgroundColor: '#1a1a1a' }}
            >
              <span className="retro-heading text-2xl text-cream">
                {currentLevel}
              </span>
            </div>
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-grass px-2 py-0.5 border-2 border-ink">
              <span className="retro-text text-xs text-cream">LEVEL</span>
            </div>
          </div>
        </div>

        {/* XP Progress Bar */}
        <div className="mb-4">
          <div className="h-6 bg-ink border-4 border-ink relative overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-grass to-green-400"
              initial={{ width: '0%' }}
              animate={{ width: `${getProgressPercent()}%` }}
              transition={{ duration: 0.5 }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="retro-text text-xs text-cream drop-shadow-lg">
                {currentXP.toLocaleString()} XP
              </span>
            </div>
          </div>
        </div>

        {/* Total Earned */}
        <div className="inline-flex items-center gap-2 bg-cream px-4 py-2 border-4 border-ink">
          <Zap className="text-yellow-500" size={20} />
          <span className="retro-heading text-lg text-grass">
            +{total_xp_earned} XP
          </span>
        </div>
      </div>

      {/* Breakdown List */}
      <div className="pixel-card p-4">
        <h3 className="retro-heading text-xs text-ink mb-4">XP BREAKDOWN</h3>
        
        <div className="space-y-3">
          {Object.entries(groupedBreakdown).map(([category, items], categoryIndex) => {
            const Icon = CATEGORY_ICONS[category] || Star;
            const color = CATEGORY_COLORS[category] || '#4B8B3B';
            
            return (
              <div key={category}>
                <div 
                  className="flex items-center gap-2 mb-2"
                  style={{ color }}
                >
                  <Icon size={16} />
                  <span className="retro-text text-xs uppercase">
                    {category.replace('_', ' ')}
                  </span>
                </div>
                
                <div className="space-y-1 pl-6">
                  {items.map((item, itemIndex) => {
                    const globalIndex = breakdown.findIndex(
                      b => b.category === category && b.description === item.description
                    );
                    const isRevealed = globalIndex < revealedItems;
                    
                    return (
                      <AnimatePresence key={itemIndex}>
                        {isRevealed && (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="flex justify-between items-center"
                          >
                            <span className="retro-body text-lg text-ink">
                              {item.description}
                            </span>
                            <span 
                              className="retro-text text-sm font-bold"
                              style={{ color }}
                            >
                              +{item.xp}
                            </span>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Level Up Celebration */}
      <AnimatePresence>
        {showLevelUp && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none"
          >
            <div className="bg-grass text-cream px-8 py-6 border-8 border-ink shadow-2xl">
              <div className="text-center">
                <motion.div
                  initial={{ rotate: 0 }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  <Star className="mx-auto text-yellow-400" size={48} />
                </motion.div>
                <h2 className="retro-heading text-2xl mt-4">LEVEL UP!</h2>
                <p className="retro-heading text-4xl mt-2">{currentLevel}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Continue Button */}
      {animationComplete && onComplete && (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={onComplete}
          className="pixel-btn pixel-btn-primary w-full mt-6"
          data-testid="continue-btn"
        >
          CONTINUE
        </motion.button>
      )}
    </div>
  );
}
